import {
  BANK_DEBIT_REFUND_DELAY_IN_DAYS,
  formatCurrencyFromCents,
  formatPaymentMethod,
  getDatePlusNDays,
  isBankDebitPaymentMethod,
  isWithinBankDebitRefundDelay,
} from "@simplyk/common";
import { ToolUIPart } from "ai";
import { useState } from "react";

import { useLocaleContext } from "@/common/contexts/LocaleContext";
import { trpc } from "@/common/helpers/trpc";
import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardAmountInput } from "@/dashboard-design-system/DashboardAmountInput";
import { ApprovalContent } from "@/features/Assistant/contexts/AssistantConfigContext";

import { AdjustTipDetails } from "../AdjustTipDetails/AdjustTipDetails";

const getAdjustTipInput = (part: ToolUIPart): { transactionId: string; suggestedNewTipAmount: number | null } | null =>
  part.type === "tool-adjustTip" &&
  typeof part.input === "object" &&
  part.input !== null &&
  "transactionId" in part.input &&
  typeof part.input.transactionId === "string"
    ? {
        transactionId: part.input.transactionId,
        suggestedNewTipAmount:
          "suggestedNewTipAmountInSmallestUnit" in part.input &&
          typeof part.input.suggestedNewTipAmountInSmallestUnit === "number"
            ? part.input.suggestedNewTipAmountInSmallestUnit
            : null,
      }
    : null;

export const useAdjustTipApproval = (part: ToolUIPart): ApprovalContent | null => {
  const { t } = useTranslate();
  const { locale, isoLocale } = useLocaleContext();
  const input = getAdjustTipInput(part);
  const [editedNewTipAmount, setEditedNewTipAmount] = useState<number | null | undefined>(undefined);
  const { data: payment } = trpc.retrieveDonorDashboardTransaction.useQuery(
    { id: input?.transactionId ?? "" },
    { enabled: Boolean(input) }
  );

  if (!input) {
    return null;
  }
  const tip = payment?.tip ?? 0;
  const organizationName = payment?.organization?.name;
  if (!payment || !organizationName || tip <= 0) {
    return { description: t("dashboard", "donorAssistant.approval.adjustTip.fallback"), canApprove: false };
  }
  const prefilledNewTipAmount =
    input.suggestedNewTipAmount !== null && input.suggestedNewTipAmount < tip ? input.suggestedNewTipAmount : tip;
  const newTipAmount = editedNewTipAmount === undefined ? prefilledNewTipAmount : editedNewTipAmount;
  const isValidAdjustment =
    newTipAmount !== null && Number.isInteger(newTipAmount) && newTipAmount >= 0 && newTipAmount < tip;
  const format = (amount: number) =>
    formatCurrencyFromCents(amount, { isoLocale, currency: payment.currency, cents: "alwaysVisible" });
  const getPaymentMethodLabel = (): string | null => {
    if (!payment.last4) {
      return null;
    }
    if (payment.paymentMethod && isBankDebitPaymentMethod(payment.paymentMethod)) {
      return formatPaymentMethod({ locale, paymentMethod: payment.paymentMethod, last4: payment.last4 });
    }
    return t("dashboard", "donorTransactionDetailDrawer.cardEndingIn", { last4: payment.last4 });
  };
  const formatDate = (value: Date) =>
    value.toLocaleDateString(locale, { month: "short", day: "numeric", year: "numeric" });
  const date = formatDate(new Date(payment.createdAtUtc));
  const isScheduled = isWithinBankDebitRefundDelay({
    paymentMethod: payment.paymentMethod,
    createdAtUtc: payment.createdAtUtc,
  });
  const refundAmount = format(isValidAdjustment ? tip - newTipAmount : 0);
  return {
    description: t("common", "donorRefund.tipAdjustment.title", { tipAmount: format(tip) }),
    details: (
      <AdjustTipDetails
        tipAmount={format(tip)}
        amountField={
          <DashboardAmountInput
            label={t("dashboard", "donorAssistant.approval.adjustTip.adjustedContribution")}
            locale={locale}
            currency={payment.currency}
            defaultValue={prefilledNewTipAmount}
            onChange={setEditedNewTipAmount}
            disableAutocomplete
            noAsterisk
          />
        }
        refundAmount={refundAmount}
        organizationName={organizationName}
        paymentLabel={`${date} · ${organizationName}`}
        paymentMethodLabel={getPaymentMethodLabel()}
        scheduledForLabel={
          isScheduled
            ? formatDate(getDatePlusNDays(new Date(payment.createdAtUtc), BANK_DEBIT_REFUND_DELAY_IN_DAYS))
            : null
        }
      />
    ),
    approveLabel: t("dashboard", "donorAssistant.approval.adjustTip.adjustButton", {
      amount: format(isValidAdjustment ? newTipAmount : prefilledNewTipAmount),
    }),
    canApprove: isValidAdjustment,
    noteValue: isValidAdjustment ? String(newTipAmount) : undefined,
  };
};
